// Cash Planning: the words and marks on the screen. Every figure behind them (the
// Uber states, each obligation's date and amount, the running balance, the red
// days, payroll cover, the variances) is computed by cashplan_page_v1 in
// db/migrations/0344. These functions only say it. CLAUDE.md rule 4.
//
// Two rules the words keep, because the page exists for cash planning:
// 1. Received, Earned and Forecast are always named, never blended. Forecast is
//    said to be forecast every time it appears.
// 2. A missing figure says it is missing and what it needs. It never reads as R0.

export type UberState = 'received' | 'earned' | 'forecast' | 'unknown'

export type UberItem = {
  kind: 'uber'
  dir: 'in'
  part: 'main' | 'secondary'
  week_start: string
  label: string
  state: UberState
  amount_zar: number | null
  known: boolean
  note: string | null
  reports_complete: boolean
}

export type ObligationItem = {
  kind: 'obligation'
  dir: 'out'
  schedule_id: string
  nominal_date: string
  label: string
  category: Category
  amount_zar: number | null
  known: boolean
  estimate: boolean | null
  basis: string | null
  needs: string | null
  skipped: boolean
  override_id: string | null
  override_note: string | null
  moved: boolean
}

export type LedgerItem = UberItem | ObligationItem

export type LedgerDay = {
  date: string
  weekday: string
  before_today: boolean
  is_today: boolean
  items: LedgerItem[]
  in_committed_zar: number
  in_forecast_zar: number
  out_zar: number
  unknown_in: number
  unknown_out: number
  unknown_out_so_far: number
  balance_committed_zar: number
  balance_projected_zar: number
  below_zero: boolean
  below_buffer: boolean
  cash_needed_zar: number | null
  cash_needed_to_zero_zar: number | null
}

export type CoverLine = {
  date: string
  amount_zar: number | null
  known: boolean
  estimate?: boolean | null
  basis: string | null
  needs: string | null
  can_pay: boolean | null
  shortfall_zar: number | null
  balance_after_committed_zar: number | null
  unknown_out_before: number | null
}

export type WeekRow = {
  week_start: string
  closes_at: string
  closed: boolean
  main_date: string | null
  main_state: UberState | null
  main_zar: number | null
  secondary_date: string | null
  secondary_state: UberState | null
  secondary_zar: number | null
  uber_total_zar: number | null
  known: boolean | null
  reports_complete: boolean | null
  shifts: number | null
  petrol_date: string | null
  petrol_zar: number | null
  petrol_known: boolean | null
  petrol_basis: string | null
  covers_petrol: boolean | null
  surplus_zar: number | null
}

export type UberVarianceRow = {
  week_start: string
  closes_at: string
  main_due: string
  secondary_due: string
  net_zar: number
  cash_zar: number
  shifts: number
  expected_total: number
  expected_main: number
  expected_secondary: number
  actual_main: number
  actual_secondary: number
  actual_total: number
  credits: string | null
  statement_covers: boolean
  variance_zar: number | null
  variance_pct: number | null
  within_tolerance: boolean | null
  cumulative_variance_zar: number | null
  finding: string
}

export type DebitVarianceRow = {
  schedule_id: string
  supplier: string
  due_date: string
  expected_zar: number | null
  expected_known: boolean
  expected_basis: string | null
  actual_zar: number | null
  actual_lines: string | null
  difference_zar: number | null
}

export type Category =
  | 'medical' | 'telematics' | 'tax' | 'owners' | 'insurance' | 'salaries'
  | 'finance' | 'petrol' | 'subscription' | 'rent' | 'other'

export type RuleKind = 'day_of_month' | 'weekday' | 'last_charge_day' | 'once'
export type BusinessDayRule = 'prior' | 'next' | 'none'
export type AmountRule =
  | 'fixed' | 'last_debit' | 'insurer_bill_else_last_debit' | 'owner_fixed_payouts' | 'owner_statement_payouts'
  | 'payroll_nett' | 'emp201_prior_month' | 'petrol_weekly' | 'standing_items' | 'manual'

export type ScheduleRow = {
  id: string
  supplier: string
  category: Category
  rule_kind: RuleKind
  day_of_month: number | null
  weekday: number | null
  once_date: string | null
  business_day_rule: BusinessDayRule
  amount_rule: AmountRule
  fixed_amount_zar: number | null
  match_pattern: string | null
  rule_key: string | null
  source: string
  active: boolean
  starts_on: string | null
  ends_on: string | null
  notes: string | null
  sort_order: number
  last_seen: { date: string; amount_zar: number } | null
  next: { date: string; amount_zar: number | null; known: boolean; basis: string | null } | null
}

export type RecurringRow = {
  stem: string
  months_seen: number
  occurrences: number
  last_date: string
  last_amount_zar: number | null
  avg_amount_zar: number | null
  min_amount_zar: number | null
  max_amount_zar: number | null
  typical_day: number | null
  suggested_pattern: string
  window_from: string
  window_to: string
}

export type Settings = {
  buffer_zar: number
  horizon_days: number
  shifts_per_car_per_day: number
  uber_secondary_zar: number
  uber_tolerance_pct: number
  variance_from: string
  forecast_trailing_weeks: number
  petrol_rate_basis: 'sasol_actual' | 'band'
  last_debit_stale_days: number
  updated_at: string | null
}

export type Petrol = {
  basis: 'sasol_actual' | 'band'
  chosen_per_shift_zar: number | null
  disagreement_pct: number | null
  sasol: {
    pattern: string | null
    window_from: string | null
    window_to: string | null
    spend_zar: number | null
    shifts: number | null
    per_shift_zar: number | null
    by_weekday: { weekday: string; isodow: number; payments: number; amount_zar: number }[]
    by_week: { week_start: string; payments: number; amount_zar: number; days: string | null; shifts: number }[]
  }
  band: {
    price_per_litre_zar: number | null
    price_from: string | null
    budget_share: number | null
    net_per_shift_zar: number | null
    net_weeks_from: string | null
    net_weeks_to: string | null
    per_shift_zar: number | null
  }
}

export type Freshness = { report_key: string; label: string; last_date: string | null; days_behind: number | null; state: string }

export type BalanceEntry = { id: string; amount_zar: number; as_at: string; note: string | null; entered_by: string | null; entered_at: string }

export type CashPlan = {
  as_at: string
  today: string
  horizon_end: string
  no_balance?: boolean
  message?: string
  settings: Settings
  balance: {
    source: 'statement' | 'entry'
    amount_zar: number
    as_at: string
    days_old: number
    statement_to: string | null
    statement_closing_zar: number | null
    entry: (BalanceEntry & { in_use: boolean }) | null
    projected_today_zar: number
    committed_today_zar: number
  }
  next14: {
    in_committed_zar: number
    in_forecast_zar: number
    out_zar: number
    unknown_out: number
    unknown_in: number
    lowest_projected_zar: number
    lowest_projected_date: string
    lowest_committed_zar: number
    lowest_committed_date: string
  } | null
  payroll_cover: {
    salary: CoverLine | null
    emp201: CoverLine | null
    committed_funds_zar: number
    monthly_payroll_zar: number | null
    months_of_cover: number | null
    emp201_included: boolean | null
  }
  ledger: LedgerDay[]
  weekly: WeekRow[]
  uber_variance: UberVarianceRow[]
  debit_variance: DebitVarianceRow[]
  petrol: Petrol
  petrol_warning: boolean
  scheduled_shifts: { on_road_cars: number; off_road_cars: number; shifts_per_week: number | null; allocation_week: string | null; basis: string } | null
  freshness: Freshness[]
  recurring_detected: RecurringRow[]
  schedule: ScheduleRow[]
  balance_entries: BalanceEntry[]
  holidays_to: string | null
  covered_to: string | null
}

export const AMOUNT_UNKNOWN = 'Amount unknown'

// The same rand the owner statements print: R1,250.00. A negative balance reads
// with a minus, because on a cash plan the sign is the point.
const ZAR = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const ZAR0 = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 })

export function formatZar(v: number | null | undefined, missing = AMOUNT_UNKNOWN): string {
  if (v === null || v === undefined || !Number.isFinite(Number(v))) return missing
  const n = Number(v)
  const body = `R${ZAR.format(Math.abs(n))}`
  return n < 0 ? `-${body}` : body
}

/** Whole rand, for the tiles where cents are noise. */
export function formatZarShort(v: number | null | undefined, missing = AMOUNT_UNKNOWN): string {
  if (v === null || v === undefined || !Number.isFinite(Number(v))) return missing
  const n = Number(v)
  const body = `R${ZAR0.format(Math.abs(Math.round(n)))}`
  return n < 0 ? `-${body}` : body
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
export const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

/** 2026-10-07 as "Wed 7 Oct". A plain date is a calendar day, never shifted by a time zone. */
export function formatDay(iso: string | null | undefined, withWeekday = true): string {
  if (!iso) return 'No date'
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  if (!m) return iso
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])]
  const wd = DAYS[new Date(Date.UTC(y, mo - 1, d)).getUTCDay()]
  return `${withWeekday ? `${wd} ` : ''}${d} ${MONTHS[mo - 1]}`
}

/** 2026-10-07 as "7 Oct 2026". */
export function formatDayYear(iso: string | null | undefined): string {
  if (!iso) return 'No date'
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  if (!m) return iso
  return `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]} ${m[1]}`
}

/** A timestamp in Johannesburg time, "Mon 5 Oct 04:00". */
export function formatSast(iso: string | null | undefined): string {
  if (!iso) return 'No time'
  const t = new Date(iso)
  if (Number.isNaN(t.getTime())) return iso
  // South Africa keeps no daylight saving, so UTC+2 is right all year.
  const s = new Date(t.getTime() + 2 * 3600 * 1000)
  const hh = String(s.getUTCHours()).padStart(2, '0')
  const mm = String(s.getUTCMinutes()).padStart(2, '0')
  return `${DAYS[s.getUTCDay()]} ${s.getUTCDate()} ${MONTHS[s.getUTCMonth()]} ${hh}:${mm}`
}

export const STATE_LABEL: Record<UberState, string> = {
  received: 'Received',
  earned: 'Earned, awaiting payout',
  forecast: 'Forecast',
  unknown: AMOUNT_UNKNOWN,
}

export type Tone = 'green' | 'red' | 'amber' | 'neutral' | 'brand'

export function stateTone(s: UberState | null | undefined): Tone {
  if (s === 'received') return 'green'
  if (s === 'earned') return 'brand'
  if (s === 'unknown') return 'amber'
  return 'neutral'
}

export function stateLabel(s: UberState | null | undefined): string {
  return s ? STATE_LABEL[s] : AMOUNT_UNKNOWN
}

/** Forecast money is shown lighter than committed money, everywhere. */
export function isForecast(item: LedgerItem): boolean {
  return item.kind === 'uber' && item.state === 'forecast'
}

export const CATEGORY_LABEL: Record<Category, string> = {
  medical: 'Medical',
  telematics: 'Telematics',
  tax: 'Tax',
  owners: 'Owners',
  insurance: 'Insurance',
  salaries: 'Salaries',
  finance: 'Finance',
  petrol: 'Petrol',
  subscription: 'Subscription',
  rent: 'Rent',
  other: 'Other',
}
export const CATEGORIES = Object.keys(CATEGORY_LABEL) as Category[]

export const AMOUNT_RULE_LABEL: Record<AmountRule, string> = {
  fixed: 'A fixed amount',
  last_debit: 'The last debited amount',
  insurer_bill_else_last_debit: 'The insurer bill for the month, else the last debit',
  owner_fixed_payouts: 'Owner fixed payouts for the prior month',
  owner_statement_payouts: 'Issued owner statement payouts for the prior month',
  payroll_nett: 'SimplePay net pay for the month',
  emp201_prior_month: 'EMP201 for the prior month, entered per date',
  petrol_weekly: 'Scheduled shifts times petrol per shift',
  standing_items: 'Payroll standing items in force',
  manual: 'Entered per date',
}
export const AMOUNT_RULES = Object.keys(AMOUNT_RULE_LABEL) as AmountRule[]

export const BUSINESS_DAY_LABEL: Record<BusinessDayRule, string> = {
  prior: 'Weekend or holiday: the business day before (we pay early)',
  next: 'Weekend or holiday: the business day after (the bank collects late)',
  none: 'Keeps its date (a card charge)',
}

export function ordinal(n: number): string {
  const s = n % 100 >= 11 && n % 100 <= 13 ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[n % 10] ?? 'th'
  return `${n}${s}`
}

/** When a row falls, in words. */
export function whenLabel(r: Pick<ScheduleRow, 'rule_kind' | 'day_of_month' | 'weekday' | 'once_date'>): string {
  if (r.rule_kind === 'day_of_month') return r.day_of_month ? `The ${ordinal(r.day_of_month)} of each month` : 'Day not set'
  if (r.rule_kind === 'weekday') return r.weekday ? `Every ${WEEKDAYS[r.weekday - 1]}` : 'Weekday not set'
  if (r.rule_kind === 'last_charge_day') return 'Monthly, on the day it last charged'
  return r.once_date ? `Once, on ${formatDayYear(r.once_date)}` : 'Date not set'
}

/** A day with money moving, a red day, or today: the ledger shows these by default. */
export function isEventful(d: LedgerDay): boolean {
  return d.items.length > 0 || d.below_buffer || d.is_today
}

/** The ledger rows to draw. Quiet days are hidden unless asked for. */
export function visibleDays(days: LedgerDay[], showQuiet: boolean): LedgerDay[] {
  return showQuiet ? days : days.filter(isEventful)
}

/** "Cash needed by Fri 23 Oct: R12,345.00", for a red day. Null on a day that is not red. */
export function cashNeededLabel(d: Pick<LedgerDay, 'date' | 'below_buffer' | 'cash_needed_zar'>): string | null {
  if (!d.below_buffer || d.cash_needed_zar === null) return null
  return `Cash needed by ${formatDay(d.date)}: ${formatZar(d.cash_needed_zar)}`
}

/** The balance source in words, so it is always plain which one is in use. */
export function balanceSourceLabel(b: CashPlan['balance']): string {
  if (b.source === 'entry' && b.entry) {
    return `Entered by ${b.entry.entered_by ?? 'someone no longer linked'}, as at ${formatDayYear(b.as_at)}`
  }
  return `Statement closing balance, ${formatDayYear(b.as_at)}`
}

/** What the payroll line says: paid, short, or cannot tell. */
export function coverVerdict(line: CoverLine | null): { tone: Tone; text: string } {
  if (!line) return { tone: 'neutral', text: 'Not in the next eight weeks' }
  if (!line.known) return { tone: 'amber', text: 'Cannot tell: the amount is unknown' }
  if (line.can_pay) return { tone: 'green', text: 'Covered by committed money' }
  return { tone: 'red', text: `Short by ${formatZar(line.shortfall_zar)} on committed money` }
}
